import { useCallback, useEffect, useMemo, useRef } from 'react';
import { apiFetch } from '../../../context/AuthContext';
import { isSameUser } from '../utils/userHelpers';
import { isServerMessageId } from '../utils/messageStatus';

const API_BASE_URL = process.env.REACT_APP_BASE_URL || 'https://nexploy-eg.com';
const SEND_DELAY_MS = 300;

const isGroupChatId = (chatId) => typeof chatId === 'string' && chatId.startsWith('group-');
const isPageVisible = () =>
  typeof document === 'undefined' || document.visibilityState === 'visible';

/**
 * The single place that sends "I've seen this thread" receipts.
 *
 * A receipt goes out when the thread is open (`isActive`), the tab is
 * visible, and there's something new to acknowledge. It is driven by the
 * messages STATE, not by whichever socket handler happened to append the
 * message — two sockets deliver every message and only one of them gets to
 * append it, which is what made the old in-handler emit fire only some of
 * the time.
 *
 * Triggers: opening the thread, a new incoming message in it, the tab
 * becoming visible/focused again, and the socket reconnecting (which also
 * calls `onResync` so receipts/messages missed while offline show up).
 */
const useReadReceipts = ({
  socket,
  chatId,
  messages,
  isActive = true,
  setConversations,
  onResync,
}) => {
  // chatId -> newest incoming message id already acknowledged
  const ackedRef = useRef(new Map());
  const timerRef = useRef(null);

  const newestIncomingId = useMemo(() => {
    let newest = 0;
    for (const m of messages || []) {
      if (m.sent || m.isSystem || !isServerMessageId(m.id)) continue;
      const id = Number(m.id);
      if (id > newest) newest = id;
    }
    return newest;
  }, [messages]);

  // Listeners/timers read the latest values from here, never a stale closure.
  const latestRef = useRef({});
  latestRef.current = { socket, chatId, isActive, setConversations, onResync, newestIncomingId };

  const flush = useCallback(() => {
    timerRef.current = null;
    const { socket: sock, chatId: id, isActive: active, setConversations: setConvs, newestIncomingId: newest } =
      latestRef.current;
    if (!id || !active || !isPageVisible()) return;

    if (isGroupChatId(id)) {
      const groupId = id.replace('group-', '');
      if (sock?.connected) {
        sock.emit('chat_group:mark_read', { groupId });
      } else {
        apiFetch(`${API_BASE_URL}/api/chat-groups/${groupId}/messages/read`, { method: 'POST' })
          .catch((err) => console.error('Failed to mark group messages as read:', err));
      }
    } else {
      // Offline: don't record it as acknowledged — the reconnect trigger retries.
      if (!sock?.connected) return;
      sock.emit('private_message:mark_read', { senderId: id });
    }

    ackedRef.current.set(id, newest);
    setConvs?.((prev) => {
      const idx = prev.findIndex((c) =>
        isGroupChatId(id) ? c.id === id : !isGroupChatId(c.id) && isSameUser(c.id, id));
      if (idx === -1 || !prev[idx].unread) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], unread: 0 };
      return next;
    });
  }, []);

  // Debounced: a burst of incoming messages / focus+visibility firing
  // together collapses into one receipt.
  const schedule = useCallback(() => {
    if (timerRef.current) return;
    timerRef.current = setTimeout(flush, SEND_DELAY_MS);
  }, [flush]);

  // Reset the ref too, not just the timer: StrictMode (dev) runs this
  // cleanup and then remounts. A stale id left in timerRef made `schedule`
  // believe a receipt was still pending, so none was ever sent again — a
  // mini-chat window (mounted with a chatId) went completely silent.
  useEffect(() => () => {
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }, []);

  // Thread opened / re-activated (e.g. mini chat restored from minimized).
  useEffect(() => {
    if (chatId && isActive) schedule();
  }, [chatId, isActive, schedule]);

  // Something new arrived in the open thread.
  useEffect(() => {
    if (!chatId || !isActive || !newestIncomingId) return;
    if (newestIncomingId > (ackedRef.current.get(chatId) || 0)) schedule();
  }, [chatId, isActive, newestIncomingId, schedule]);

  // Messages that arrived while the tab was hidden are acknowledged when the
  // user actually comes back to it.
  useEffect(() => {
    const onVisible = () => {
      if (isPageVisible()) schedule();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
    };
  }, [schedule]);

  // Reconnect: events emitted while we were offline are gone for good, so
  // re-pull the open thread (missed messages + read receipts for OUR
  // messages) and re-send our own receipt.
  useEffect(() => {
    if (!socket) return undefined;
    const onConnect = () => {
      const { chatId: id, onResync: resync } = latestRef.current;
      if (!id) return;
      resync?.(id);
      schedule();
    };
    socket.on('connect', onConnect);
    return () => socket.off('connect', onConnect);
  }, [socket, schedule]);
};

export default useReadReceipts;
